import { Card, CardContent, Badge, Button, cn, CLIENT_LOCALE } from '@yeshuman/sdk'
import { Symptom } from '../hooks/use-symptoms'
import { Edit, Trash2, Stethoscope } from 'lucide-react'

interface SymptomCardProps {
  symptom: Symptom
  onEdit?: (symptom: Symptom) => void
  onDelete?: (symptomId: string) => void
  isHighlighted?: boolean
}

export function SymptomCard({ symptom, onEdit, onDelete, isHighlighted }: SymptomCardProps) {
  const getSeverityBadgeColor = () => {
    switch (symptom.severity) {
      case 'mild':
        return 'bg-green-500/10 text-green-700 dark:text-green-400'
      case 'moderate':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
      case 'severe':
        return 'bg-orange-500/10 text-orange-700 dark:text-orange-400'
      case 'very_severe':
        return 'bg-red-500/10 text-red-700 dark:text-red-400'
      default:
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
    }
  }

  const getSeverityLabel = () => {
    switch (symptom.severity) {
      case 'mild':
        return 'Mild'
      case 'moderate':
        return 'Moderate'
      case 'severe':
        return 'Severe'
      case 'very_severe':
        return 'Very Severe'
      default:
        return symptom.severity
    }
  }

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    const dateOnly = new Date(date)
    dateOnly.setHours(0, 0, 0, 0)
    const todayOnly = new Date(today)
    todayOnly.setHours(0, 0, 0, 0)
    const yesterdayOnly = new Date(yesterday)
    yesterdayOnly.setHours(0, 0, 0, 0)
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
      return `Today at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return `Yesterday at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else {
      return date.toLocaleString(CLIENT_LOCALE, { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    }
  }

  const getLocationDisplay = () => {
    if (symptom.anatomy.parent) {
      return `${symptom.anatomy.parent} > ${symptom.anatomy.name}`
    }
    return symptom.anatomy.name
  }

  return (
    <Card
      className={cn(
        "transition-all duration-300 hover:shadow-md",
        isHighlighted && "ring-2 ring-primary ring-offset-2 animate-pulse"
      )}
    >
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={cn("flex items-center gap-1", getSeverityBadgeColor())}>
                <Stethoscope className="h-3.5 w-3.5" />
                {getSeverityLabel()}
              </Badge>
              <span className="text-sm text-muted-foreground">{formatDateTime(symptom.occurred_at)}</span>
            </div>
            
            <div>
              <h3 className="font-semibold capitalize">{symptom.symptom_type}</h3>
              <p className="text-sm text-muted-foreground">
                Location: {getLocationDisplay()}
                {symptom.anatomy.medical_term && (
                  <span className="ml-2 italic">({symptom.anatomy.medical_term})</span>
                )}
              </p>
              {symptom.notes && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{symptom.notes}</p>
              )}
            </div>
          </div>
          
          {(onEdit || onDelete) && (
            <div className="flex gap-1 ml-4">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(symptom)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(symptom.id)}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

